import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useRef, useState } from "react";
import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import Select from "react-select";
import { Modal, ModalHeader, ModalBody, ModalFooter } from "reactstrap";
import { ISSUE_PRIORITY } from "../../../_constants/qmsConstants";
import cmtIssueActions from "../../../_actions/_qmsActions/cmtIssueActions";
import AlertModal from "../../_common/AlertModal";
import { useMemo } from "react";
import CmtIssueStagePrepopulate from "./CmtIssueStagePrepopulate";
import CmtLinkedDocumentEditor from "./CmtLinkedDocumentEditor";
import qmsWorkflowActions from "../../../_actions/_qmsActions/qmsWorkflowActions";
import ReactDatePicker from "react-datepicker";
import CustomDatePickerInput from "../../_common/CustomDatePickerInput";
import BadgeContainer from "../../_common/BadgeContainer";
import Badge from "../../_common/Badge";

export default function CmtModal_AddChildIssue(props) {
  const { modalState, toggleModal, tabId } = props;

  const dispatch = useDispatch();

  const isInitialRender = useRef(true); // To skip the first render in useEffect()

  const adding = useSelector((state) => state.qms.adding);
  const isAdding = useRef(adding); // To capture previous 'adding' value in useEffect()

  const fetching = useSelector((state) => state.qms.fetching);
  const qmsUser = useSelector((state) => state.qms.qmsUser || null);
  const qmsTeams = useSelector((state) =>
    modalState.data.issue
      ? state.qms.qmsTeams[modalState.data.issue.divisionId] || null
      : null
  );

  const qmsTeamOptions = useMemo(
    () => (qmsTeams || []).map((qt) => ({ label: qt.name, value: qt.qmsTeamId })),
    [qmsTeams]
  );

  const [subject, setSubject] = useState("");
  const [primaryOwner, setPrimaryOwner] = useState(null);
  const [description, setDescription] = useState("");
  const [priorityOptions, setPriorityOptions] = useState([]);
  const [selectedPriority, setPriority] = useState(null);
  const [deadline, setDeadline] = useState(null);
  const [linkedDocuments, setLinkedDocuments] = useState([]);
  const [otherLinkedDocuments, setOtherLinkedDocuments] = useState("");
  const [files, setFiles] = useState([]);
  const [formErrors, setFormErrors] = useState({
    subject: null,
  });

  const [starter, setStarter] = useState("none");
  const [selectedWorkflow, setWorkflow] = useState(null);
  const [selectedPresetStage, setPresetStage] = useState(null);
  const [initialStageTitle, setInitialStageTitle] = useState("Init. Assignment");
  const [selectedAssignTo, setAssignTo] = useState(null);

  useEffect(() => {
    setPriorityOptions(
      Object.values(ISSUE_PRIORITY).map((ip) => ({
        label: ip.displayName,
        value: ip.value,
        name: ip.name,
      }))
    );
  }, []);

  useEffect(() => {
    if (adding) isAdding.current = adding;
  }, [adding]);

  useEffect(() => {
    if (isInitialRender.current) {
      isInitialRender.current = false;
      return;
    }
    if (isAdding.current && !adding) {
      dispatch(cmtIssueActions.getAll(modalState.data.issue.divisionId));
      dispatch(cmtIssueActions.get(modalState.data.issue.issueId));
      toggleModal();
    }
  }, [adding]);

  const handleBlur = (e) => validateForm(e.target.name);

  const formIsIncomplete = () =>
    Object.values(formErrors).some((e) => e) ||
    !subject ||
    (starter === "workflow" && !selectedWorkflow) ||
    (starter === "stage" && !selectedAssignTo);

  const validateForm = (fieldName) => {
    let standardError = "Required";
    switch (fieldName) {
      case "subject":
        setFormErrors({ ...formErrors, subject: subject ? null : standardError });
        break;
      default:
        break;
    }
  };

  const handleUpdate_linkedDocuments = (updatedData) => {
    if (!updatedData) return;
    setLinkedDocuments(updatedData.linkedDocuments || []);
    setOtherLinkedDocuments(updatedData.otherLinkedDocuments || "");
  };

  const handleChange_fileInput = (e) => {
    // Must shallow-copy
    let existFiles = [...files];
    const newFiles = Array.from(e.target.files);
    const _files = existFiles.concat(newFiles);
    setFiles(_files);
  };

  const removeFileToUpload = (fileIndex) => {
    // Must shallow-copy
    let _files = [...files];
    _files.splice(fileIndex, 1);
    setFiles(_files);
  };

  const handleClick_submit = async (e) => {
    e.preventDefault();
    if (!modalState.data.issue) return;
    const newIssueData = {
      divisionId: modalState.data.issue.divisionId,
      priority: selectedPriority ? selectedPriority.name : "LOW", // If unselected, default to "LOW"
      primaryOwnerId: primaryOwner ? primaryOwner.value : null,
      parentIssueId: modalState.data.issue.issueId, // Cannot use this modal to create a child
      initiatedByQmsUserId: qmsUser.qmsUserId,
      subject,
      description,
      proposedChanges: "",
      deadline: deadline ? new Date(deadline).toISOString() : null,
      categoryIds: [], // No categories for children
      linkedDocuments,
      otherLinkedDocuments,
      stagePrepopulationType: starter,
      appliedWorkflowId:
        starter === "workflow" && selectedWorkflow ? selectedWorkflow.value : null,
      assignToQmsTeamId:
        starter === "stage" && selectedAssignTo ? selectedAssignTo.value : null,
      initialStageTitle: starter === "stage" && initialStageTitle,
      skipIntakeReview: true,
    };

    const formData = new FormData();
    files.forEach((file, i) => formData.append(i, file));
    Object.entries(newIssueData).forEach(([key, value]) => {
      if (Array.isArray(value)) {
        value.forEach((v, i) => {
          if (v !== null && typeof v === "object" && !Array.isArray(v)) {
            Object.entries(v).forEach(([oKey, oValue]) => {
              formData.append(`${key}[${i}].${oKey}`, oValue);
            });
          } else formData.append(`${key}[${i}]`, v);
        });
      } else formData.append(key, value);
    });
    dispatch(cmtIssueActions.add(formData));
  };

  const closeBtn = (
    <button className="close" onClick={toggleModal}>
      <FontAwesomeIcon icon="times" className="fa-fw text-white" />
    </button>
  );

  return (
    <Modal isOpen={modalState.showModal} toggle={toggleModal} fade={false} size={"xl"}>
      <ModalHeader toggle={toggleModal} className="bg-info text-white" close={closeBtn}>
        <FontAwesomeIcon icon="plus" className="fa-fw mr-2" />
        Add Child Issue
      </ModalHeader>
      <ModalBody>
        <div className="row mb-2">
          <div className="form-group col-md-8 mb-0 pr-1">
            <label htmlFor="subject" className="mb-0 font-weight-bold">
              Subject:*
            </label>
            <input
              type="text"
              name="subject"
              id="subject"
              className="form-control"
              value={subject || ""}
              maxLength="150"
              onBlur={handleBlur}
              onChange={(e) => setSubject(e.target.value)}
            />
            {formErrors.subject && (
              <span className="error-small">
                <small>{formErrors.subject}</small>
              </span>
            )}
          </div>
          <div className="form-group col-md-4 mb-0 pl-1">
            <label htmlFor="primaryOwner" className="mb-0">
              Primary Owner:
            </label>
            <Select
              name="primaryOwner"
              id="primaryOwner"
              value={primaryOwner}
              options={qmsTeamOptions}
              onChange={(selected) => setPrimaryOwner(selected)}
              isClearable
              styles={{
                // Fixes the overlapping problem of the component
                menu: (provided) => ({ ...provided, zIndex: 9999 }),
              }}
            />
          </div>
        </div>
        <div className="row mb-2">
          <div className="form-group col-12 mb-0">
            <label htmlFor="subject" className="mb-0">
              Description:
            </label>
            <textarea
              id="description"
              name="description"
              className="form-control"
              rows="3"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>
        </div>
        <div className="row mb-2">
          <div className="form-group col-md-4 mb-0 pr-1">
            <label htmlFor="priority" className="mb-0">
              Priority:
            </label>
            <Select
              name="priority"
              id="priority"
              value={selectedPriority}
              options={priorityOptions}
              onChange={(selectedPriority) => setPriority(selectedPriority)}
              isClearable
              styles={{
                // Fixes the overlapping problem of the component
                menu: (provided) => ({ ...provided, zIndex: 9999 }),
              }}
            />
          </div>
          <div className="form-group col-md-4 mb-0 pl-1">
            <label htmlFor="deadline" className="mb-0">
              Deadline:
            </label>
            <div className="ml-auto">
              <ReactDatePicker
                customInput={
                  <CustomDatePickerInput isClearable onClear={() => setDeadline(null)} />
                }
                name="deadline"
                id="deadline"
                selected={deadline}
                dateFormat="MM/dd/yyyy (E)"
                onChange={(date) => setDeadline(date)}
              />
            </div>
          </div>
        </div>

        <div className="row mb-2">
          <div className="form-group col-12 mb-0">
            <label className="mb-0">
              Linked Documents (if applicable):
            </label>
            <div className="card">
              <div className="card-body p-3">
                <CmtLinkedDocumentEditor
                  divisionId={modalState.data.issue.divisionId}
                  data={null}
                  onUpdate={handleUpdate_linkedDocuments}
                />
              </div>
            </div>
          </div>
        </div>
        <div className="card mb-2">
          <div className="card-body p-3">
            <div className="row">
              <div className="form-group col-12 mb-2">
                <label htmlFor="file" className="mb-0">
                  Attachments:
                </label>
                <div className="custom-file">
                  <input
                    type="file"
                    id="file"
                    name="file"
                    className="custom-file-input"
                    onChange={handleChange_fileInput}
                    accept=".docx,.xlsx,.txt,.csv,.pdf,.jpg,.jpeg,.gif"
                    multiple
                  />
                  <label htmlFor="file" className="custom-file-label">
                    Choose files...
                  </label>
                </div>
              </div>
            </div>
            {files.length > 0 && (
              <div className="row">
                <div className="col">
                  <BadgeContainer>
                    {files.map((f, index) => (
                      <Badge
                        className="mr-1"
                        key={`fileLabelIndex_${index}`}
                        itemId={index}
                        clickItem={() => {}}
                        deleteItem={removeFileToUpload}
                        label={f.name}
                        showDelete={true}
                      />
                    ))}
                  </BadgeContainer>
                </div>
              </div>
            )}
          </div>
        </div>
        <CmtIssueStagePrepopulate
          divisionId={modalState.data.issue.divisionId}
          tabId={tabId}
          starter={starter}
          setStarter={setStarter}
          selectedWorkflow={selectedWorkflow}
          setWorkflow={setWorkflow}
          selectedPresetStage={selectedPresetStage}
          setPresetStage={setPresetStage}
          initialStageTitle={initialStageTitle}
          setInitialStageTitle={setInitialStageTitle}
          selectedAssignTo={selectedAssignTo}
          setAssignTo={setAssignTo}
        />
      </ModalBody>
      <ModalFooter className="bg-lightgray">
        <div className="form-group form-row form-inline float-right mb-0">
          <button
            type="button"
            name="submit"
            id="submit"
            className="btn btn-primary mr-2"
            onClick={handleClick_submit}
            disabled={formIsIncomplete()}
          >
            {fetching && (
              <span className="mr-2 mt-1">
                <FontAwesomeIcon
                  icon="spinner"
                  className="loading-spinner"
                  style={{ color: "white" }}
                />
              </span>
            )}
            <b>Submit</b>
          </button>
          <button
            type="button"
            name="cancel"
            id="cancel"
            className="btn btn-secondary"
            onClick={toggleModal}
          >
            Cancel
          </button>
        </div>
      </ModalFooter>
    </Modal>
  );
}
